"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import {
  SessionList,
  type SessionListItem,
} from "@/components/session-picker/SessionList";
import {
  ensureDashboardSnapshots,
  listSnapshotSessions,
} from "@/lib/server-actions/dashboardSnapshotActions";

/**
 * /my 대시보드 홈 헤더의 "지난 회차 보기" 버튼 (JCG-3283).
 *
 * 마운트 시 지난 회차 스냅샷을 멱등 캡처(ensureDashboardSnapshots)한 뒤, 본인 스냅샷이 있는 과거
 * 회차를 조회한다. 스냅샷이 하나라도 있으면 검색창 왼쪽 헤더 액션 슬롯(headerActionSlot)에 트리거
 * 버튼을 createPortal 한다(LanguageToggle과 동일한 헤더 포털 패턴, null 가드 필수). 회차 선택 시
 * /my/snapshots/[id] 읽기전용 화면으로 이동한다.
 *
 * 헤더 슬롯이 없는(모바일/셸 밖·SSR 첫 렌더) 경우와 스냅샷이 없는 경우에는 아무것도 렌더하지 않는다.
 */
export default function PreviousSessionButton() {
  const router = useRouter();
  const { headerActionSlot } = useArticleShellSlots();
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 지난 회차 스냅샷을 먼저 확보(멱등)한 뒤 목록을 조회한다.
        await ensureDashboardSnapshots();
        const list = await listSnapshotSessions();
        if (alive) setSessions(list);
      } catch {
        // best-effort — 실패 시 버튼 미노출
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!headerActionSlot || sessions.length === 0) return null;

  return createPortal(
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <History className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          지난 회차 보기
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>지난 회차 보기</DialogTitle>
          <DialogDescription>
            이전 회차의 대시보드를 읽기 전용으로 볼 수 있어요.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-1">
          <SessionList
            sessions={sessions}
            onSelect={(session) => {
              setOpen(false);
              router.push(`/my/snapshots/${encodeURIComponent(session.id)}`);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>,
    headerActionSlot,
  );
}
